import { QUERY_PROGRESS_FAILED } from "core/consts";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useEffect } from "react";
import { FormWatcher, isValid } from "react-forms-state";
import { useTranslations } from "translations";

type ShowNotificationEvent = (validation: Record<string, any>) => void;

function getValidationDetailsForMessage(
  validation: Record<string, any>,
): unknown {
  if (validation?.infos && typeof validation.infos === "object") {
    return Object.keys(validation.infos)
      .filter((s) => !Object.is(validation.infos[s], true))
      .join(", ");
  }
  return validation;
}

export const getMessage = (
  validation: Record<string, any>,
  message: string,
  details?: boolean,
) => {
  if (!details) {
    return message;
  }

  return `${message} : ${JSON.stringify(
    getValidationDetailsForMessage(validation),
  )}`;
};

export function ValidationNotification({
  message,
  onShowNotification,
  showDetails,
  validation,
}: {
  message?: string;
  onShowNotification?: ShowNotificationEvent;
  showDetails?: boolean;
  validation: Record<string, any>;
}) {
  const translations = useTranslations();

  const toast = useToast();
  useEffect(() => {
    if (!isValid(validation)) {
      const text = message || translations.actions.validationError;

      toast({
        color: "danger",
        message: getMessage(validation, text, showDetails),
      });

      if (onShowNotification) {
        onShowNotification(validation);
      }
    }
  }, [validation]);

  return null;
}

export const FormValidationNotification = ({
  message,
  onShowNotification,
  showDetails,
}: {
  message?: string;
  onShowNotification?: ShowNotificationEvent;
  showDetails?: boolean;
}) => (
  <FormWatcher watchPath="">
    {({ validation }: { validation: Record<string, any> }) => (
      <ValidationNotification
        message={message}
        showDetails={showDetails}
        validation={validation}
        onShowNotification={onShowNotification}
      />
    )}
  </FormWatcher>
);

export function ErrorNotification({
  message,
  queryProgress,
}: {
  message: string;
  queryProgress: string;
}) {
  const toast = useToast();

  useEffect(() => {
    if (queryProgress === QUERY_PROGRESS_FAILED) {
      toast({ color: "primary", message });
    }
  }, [queryProgress, message]);

  return null;
}
